import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Save, Download, Printer, Mail, RotateCcw, FileSignature, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { downloadBlob } from "@/lib/download";
import { DataConsentSheet } from "@/components/DataConsentSheet";
import { DataConsentSignDialog } from "@/components/DataConsentSignDialog";

const EDITABLE = ["user_name", "user_dni", "user_address", "user_email", "consent_contact",
    "contact_choice", "contact_name", "consent_doctors", "consent_marketing"];
const todayMadrid = () => new Date().toLocaleDateString("es-ES",
    { timeZone: "Europe/Madrid", day: "2-digit", month: "2-digit", year: "numeric" });

const missingFields = (d) => {
    const m = [];
    if (!(d.user_name || "").trim()) m.push("Nombre del usuario");
    if (!(d.user_dni || "").trim()) m.push("DNI");
    if (!(d.user_address || "").trim()) m.push("Domicilio");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((d.user_email || "").trim())) m.push("Correo electrónico válido");
    if (d.consent_contact && d.contact_choice !== "person") m.push("Persona de contacto autorizada");
    if (d.contact_choice === "person" && !(d.contact_name || "").trim()) m.push("Nombre del familiar/cuidador (o marca «Ninguna»)");
    return m;
};

export default function DataConsentPanel({ patientId, kiosk = false, apiClient = null, authHeaders = null,
    onChange, onSigned, currentUserRole = null }) {
    const client = useRef(apiClient || api).current;
    const [doc, setDoc] = useState(null);
    const [saving, setSaving] = useState(false);
    const [signOpen, setSignOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailValue, setEmailValue] = useState("");
    const [sending, setSending] = useState(false);
    const base = kiosk ? "/kiosk/data-consent" : `/patients/${patientId}/data-consent`;
    const cfg = useCallback((extra = {}) => ({ ...extra, ...(authHeaders ? { headers: authHeaders } : {}) }), [authHeaders]);
    const isSigned = doc?.status === "signed";
    const canReopen = !kiosk && (currentUserRole === "directivo" || currentUserRole === "secretaria");

    const load = useCallback(async () => {
        try { const { data } = await client.get(base, cfg()); setDoc(data); }
        catch (e) { toast.error(e.response?.data?.detail || "No se pudo cargar el documento"); }
    }, [client, base, cfg]);
    useEffect(() => { if (kiosk || patientId) { load(); } }, [load, kiosk, patientId]);

    const set = (patch) => setDoc((d) => ({ ...d, ...patch }));
    const persist = async () => {
        const payload = Object.fromEntries(EDITABLE.map((k) => [k, doc[k] ?? null]));
        const { data } = await client.put(base, payload, cfg());
        setDoc(data);
        return data;
    };
    const save = async () => {
        setSaving(true);
        try { await persist(); toast.success("Datos guardados"); onChange && onChange(); }
        catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
        finally { setSaving(false); }
    };
    const openSign = () => {
        const m = missingFields(doc);
        if (m.length) { toast.error(`Completa: ${m.join(", ")}`); return; }
        setSignOpen(true);
    };
    const sign = async (payload) => {
        setSubmitting(true);
        try {
            await persist();
            const { data } = await client.post(`${base}/sign`, payload, cfg());
            setDoc(data); setSignOpen(false);
            toast.success("Documento firmado y guardado en la ficha");
            onSigned && onSigned(data); onChange && onChange();
        } catch (e) { toast.error(e.response?.data?.detail || "Error al firmar"); }
        finally { setSubmitting(false); }
    };
    const fetchPdf = async () => (await client.get(`${base}/pdf`, cfg({ responseType: "blob" }))).data;
    const download = async () => {
        try { downloadBlob(await fetchPdf(), `Proteccion-datos-${isSigned ? "firmado" : "borrador"}.pdf`); }
        catch (e) { toast.error("No se pudo descargar el PDF"); }
    };
    const printPdf = async () => {
        try {
            const w = window.open(URL.createObjectURL(new Blob([await fetchPdf()], { type: "application/pdf" })), "_blank");
            if (!w) { toast.error("Permite ventanas emergentes para imprimir"); return; }
            const trigger = () => { try { w.focus(); w.print(); } catch (_e) { } };
            w.addEventListener("load", trigger); setTimeout(trigger, 900);
        } catch (e) { toast.error("No se pudo abrir el PDF para imprimir"); }
    };
    const sendEmail = async () => {
        if (!emailValue.includes("@")) { toast.error("Introduce un email válido"); return; }
        setSending(true);
        try {
            await client.post(`${base}/send-email`, { email: emailValue.trim() }, cfg());
            toast.success(`Enviado a ${emailValue}`); setEmailOpen(false); load();
        } catch (e) { toast.error(e.response?.data?.detail || "No se pudo enviar el email"); }
        finally { setSending(false); }
    };
    const reopen = async () => {
        if (!window.confirm("¿Reabrir el documento firmado para volver a firmarlo? El PDF firmado seguirá en Documentos.")) return;
        try { const { data } = await client.post(`${base}/reopen`, {}, cfg()); setDoc(data); toast.success("Documento reabierto"); onChange && onChange(); }
        catch (e) { toast.error(e.response?.data?.detail || "No se pudo reabrir"); }
    };

    if (!doc) return <Card className="p-5 text-sm text-muted-foreground" data-testid="data-consent-loading">Cargando protección de datos…</Card>;
    return (
        <Card className="p-4 sm:p-5 space-y-4" data-testid="data-consent-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="uppercase-label flex items-center gap-1"><ShieldCheck size={12} /> Protección de datos</div>
                    <div className="font-semibold mt-0.5">Consentimiento expreso pacientes</div>
                </div>
                {isSigned ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" variant="outline" data-testid="dc-status-signed">
                        <CheckCircle2 size={12} className="mr-1" /> Firmado el {new Date(doc.signed_at).toLocaleDateString("es-ES", { timeZone: "Europe/Madrid" })}
                        {doc.signed_by_kiosk ? " · Quiosco" : ""}
                    </Badge>
                ) : (
                    <Badge variant="outline" className="text-amber-800 border-amber-300" data-testid="dc-status-draft">Pendiente de firma</Badge>
                )}
            </div>
            {!isSigned && <p className="text-xs text-muted-foreground">Lee el documento, revisa o completa tus datos (ya rellenados desde la ficha), marca las autorizaciones que desees y firma al final.</p>}
            <div className="overflow-x-auto rounded-md bg-secondary/30 p-2 sm:p-4">
                <DataConsentSheet doc={doc} text={doc.text} set={set} readOnly={isSigned} dateLabel={doc.signed_date || todayMadrid()} />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
                {!isSigned && (
                    <>
                        <Button variant="outline" onClick={save} disabled={saving} data-testid="dc-save"><Save size={14} className="mr-1" /> {saving ? "Guardando…" : "Guardar datos"}</Button>
                        <Button className="btn-cta" onClick={openSign} data-testid="dc-open-sign"><FileSignature size={14} className="mr-1" /> Firmar documento</Button>
                    </>
                )}
                {isSigned && (
                    <>
                        <Button variant="outline" onClick={download} data-testid="dc-download"><Download size={14} className="mr-1" /> Descargar PDF</Button>
                        <Button variant="outline" onClick={printPdf} data-testid="dc-print"><Printer size={14} className="mr-1" /> Imprimir</Button>
                        <Button variant="outline" onClick={() => { setEmailValue(doc.email_sent_to || doc.user_email || ""); setEmailOpen(true); }} data-testid="dc-email"><Mail size={14} className="mr-1" /> Enviar por email</Button>
                        {canReopen && <Button variant="ghost" onClick={reopen} data-testid="dc-reopen"><RotateCcw size={14} className="mr-1" /> Reabrir</Button>}
                    </>
                )}
            </div>
            <DataConsentSignDialog open={signOpen} onOpenChange={setSignOpen} patientName={doc.user_name} onSign={sign} submitting={submitting} kiosk={kiosk} />
            <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                <DialogContent data-testid="dc-email-dialog">
                    <DialogHeader><DialogTitle>Enviar documento firmado por email</DialogTitle></DialogHeader>
                    <Input type="email" value={emailValue} onChange={(e) => setEmailValue(e.target.value)} data-testid="dc-email-input" />
                    <DialogFooter>
                        <Button className="btn-cta" onClick={sendEmail} disabled={sending} data-testid="dc-email-send"><Mail size={14} className="mr-1" /> {sending ? "Enviando…" : "Enviar"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
